import { NgxsOnInit, Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { LoadAppInfo } from './app-info.actions';
import { AppInfoModel } from '../models/app-info.model';
import { AppInfoApiService } from '../services/app-info-api.service';
import { AppVersionDto } from '../models/app-version-dto';
import { Observable, tap } from 'rxjs';
import { ReloadService } from '../services/reload.service';
import { BrowserLocationService } from '../services/browser-location.service';
import { OpenModal } from './actions/modal.actions';
import { NewVersionAvailableModalComponent } from '../components/new-version-available-modal/new-version-available-modal.component';
import { AppRouteEnum } from '../enums/app-route.enum';
import { AuthService } from 'src/modules/security/services/auth.service';
import { LoginSuccess } from 'src/modules/security/actions/auth-actions';

@State<AppInfoModel>({
  name: 'appInfo',
  defaults: {
    version: ''
  }
})
@Injectable()
export class AppInfoState implements NgxsOnInit {
  private readonly appInfoApiService = inject(AppInfoApiService);
  private readonly reloadService = inject(ReloadService);
  private readonly browserLocationService = inject(BrowserLocationService);
  private readonly authService = inject(AuthService);

  public ngxsOnInit(ctx: StateContext<AppInfoModel>): void {
    ctx.dispatch(new LoadAppInfo());
  }

  @Selector()
  public static appVersion(state: AppInfoModel): string {
    return state.version;
  }

  // The version is otherwise fetched only on bootstrap. A tab left open on the login page across a release
  // would never notice the new version once the user logs in, so re-check right after a successful login.
  // Intentionally not returned: a failing /version request must not fail LoginSuccess for other listeners.
  @Action(LoginSuccess)
  public recheckVersionAfterLogin(ctx: StateContext<AppInfoModel>): void {
    ctx.dispatch(new LoadAppInfo());
  }

  @Action(LoadAppInfo)
  public loadAppInfo(ctx: StateContext<AppInfoModel>): Observable<AppVersionDto> {
    // Captured before the new fetch completes: this is "the version last seen by this browser"
    // (restored from localStorage by NgxsStoragePluginModule on a fresh bootstrap, or left over
    // from the previous fetch within the same session).
    const previousVersion = ctx.getState().version;

    return this.appInfoApiService.getAppVersion().pipe(
      tap((appVersion: AppVersionDto) => {
        const newVersion = appVersion.version;

        // Persist the new version before acting on the mismatch, so a reload always comes back
        // to a consistent, already-updated stored version instead of looping.
        ctx.patchState({ version: newVersion });

        if (previousVersion && previousVersion !== newVersion) {
          this.handleNewVersionDetected(ctx);
        }
      })
    );
  }

  private handleNewVersionDetected(ctx: StateContext<AppInfoModel>): void {
    // No modal can be shown (SafeDialogService blocks all dialogs when unauthenticated) and no
    // in-progress work to lose: refresh immediately and silently on the login page or whenever
    // the user isn't authenticated (e.g. an expired session on a deep link after a release).
    if (this.browserLocationService.getPathname().startsWith(`/${AppRouteEnum.Login}`) || !this.authService.isAuthenticated()) {
      this.reloadService.reload();
      return;
    }

    ctx.dispatch(
      new OpenModal(NewVersionAvailableModalComponent, {
        closeByEscape: false,
        closeByBackdropClick: false,
        closeOnNavigation: false
      })
    );
  }
}
